import { Injectable, inject } from "@angular/core";
import {
    chain,
    Dictionary,
    flatMap,
    groupBy,
    values
} from "lodash";
import { forkJoin, Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { MarknadsomradeDTO } from "~app/common/models/common/MarknadsomradeDTO";
import { Anskaffningsformer, Arbetsordertyper } from "~app/common/models/constants";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { StringUtils } from "~app/common/utils/string-utils";
import { ApiClientService } from "../../../../common/services/api-client.service";
import { IMottagningsplats } from "../components/tpl-filter/tpl-filter.models";
import { Destinering, TplAoRowsModel } from "../components/tpl-omdestinera/components/begransa-virkesorderrader-modal/begransa-virkesorderrader-model";
import {
    IKeyMottagninsplatsFilterValue,
    IKeyValue,
    IKeyValueGroup,
    ITplArbetsorder,
    ITplArbetsorderDetails,
    ITplDestinering,
    ITplHandelser,
    ITplMottagningsplatsLeveransplan
} from "../models/dtos";
import {
    IBefraktare,
    ITplDestineringRuttQueryResult,
    ITplDestineringsomradefilterQueryResult,
    ITplKvvNyRuttQueryResult,
    ITplProduktionsomradefilterQueryResult,
    ITplSearchArbetsorderQueryResult,
    TplArbetsorderDetailsQuery,
    TplDestineringRuttQuery,
    TplDestineringsomradefilterQuery,
    TplDestineringsomradenMapQuery,
    TplDestineringsomradenMapQueryResult,
    TplDestineringsraderQuery,
    TplGetHandelserQuery,
    TplKvvNyRuttQuery,
    TplProduktionsomradefilterQuery,
    TplSearchArbetsorderQuery,
    VirkesorderBegransningsforslagQuery
} from "../models/queries";

@Injectable({
    providedIn: "root"
})

export class TplService {
    private readonly apiClient = inject(ApiClientService);

    private readonly bransleGrupper = [
        "Grot",
        "Träddelar"
    ];

    private handelssortimentDict: Dictionary<IKeyValueGroup[]>;
    private maskinlagDict: Dictionary<IKeyValue[]>;
    private mottagningsplatsDict: Dictionary<IKeyValue[]>;
    private befraktareDict: Dictionary<IBefraktare[]>;

    searchArbetsorder(query: TplSearchArbetsorderQuery): Observable<ITplSearchArbetsorderQueryResult> {
        const toDate = (raw: any): Date => {
            if (!raw) {
                return null;
            }
            return new Date(raw);
        };

        const search$ = this.apiClient
            .get<ITplSearchArbetsorderQueryResult>("transportplanera/arbetsorder", false, query)
            .pipe(
                tap(x => x.arbetsorderList.forEach(ao => {
                    ao.transportPaborjadDatum = toDate(ao.transportPaborjadDatum);
                    ao.transportAvslutadDatum = toDate(ao.transportAvslutadDatum);
                    ao.periodStart = toDate(ao.periodStart);
                    ao.periodSlut = toDate(ao.periodSlut);
                    ao.skordningPlaneratStartDatum = toDate(ao.skordningPlaneratStartDatum);
                    ao.skordningPlaneratSlutDatum = toDate(ao.skordningPlaneratSlutDatum);
                    ao.skotningPlaneratStartDatum = toDate(ao.skotningPlaneratStartDatum);
                    ao.skotningPlaneratSlutDatum = toDate(ao.skotningPlaneratSlutDatum);
                })),
                tap(x => {
                    this.handelssortimentDict = groupBy(x.handelssortimentList, hs => hs.key);
                }),
                tap(x => {
                    this.maskinlagDict = groupBy(x.maskinlagList, hs => hs.key);
                }),
                tap(x => {
                    this.mottagningsplatsDict = groupBy(x.mottagningsplatsList, hs => hs.key);
                }));

        const allBefraktare$ = this.apiClient
            .getCached<IBefraktare[]>("transport/befraktare", false)
            .pipe(tap(x => {
                this.befraktareDict = groupBy(x, x1 => x1.id);
            }));

        return forkJoin([
            search$,
            allBefraktare$
        ])
            .pipe(map(results => results[0]));
    }

    refresh(arbetsorderIdList: number[]): Observable<ITplArbetsorder[]> {
        return this.apiClient
            .post<number[], ITplArbetsorder[]>("transportplanera/arbetsorder/refresh", arbetsorderIdList);
    }

    resolveMottagningsplats(mottagningsplatsId: number): IKeyValue {
        return this.mottagningsplatsDict[mottagningsplatsId][0];
    }

    resolveMottagningsplatsList(mottagningsplatserList: IMottagningsplats[]): IKeyMottagninsplatsFilterValue[] {
        const res = [] as IKeyMottagninsplatsFilterValue[];
        mottagningsplatserList.forEach(ml => {
            const id = this.mottagningsplatsDict[ml.id][0].key;
            let name = this.mottagningsplatsDict[ml.id][0].value;
            if (ObjectUtils.hasValue(ml.foradlatHandelssortimentId)) {
                name = StringUtils.suffixString(name, "//FLIS", 20);
            }

            const mp = {
                key: `${id}/${ml.foradlatHandelssortimentId}`,
                value: name
            };

            if (!ObjectUtils.hasValue(res.find(x => x.key === mp.key))) {
                res.push(mp);
            }
        });

        return res;
    }

    resolveApplicableMottagningsplatser(arbetsorderList: ITplArbetsorder[]): ITplDestinering[] {
        return chain(arbetsorderList)
            .flatMap(x => x.handelssortiment)
            .flatMap(x => x.destineringar)
            .uniqBy(x => x.mottagningsplatsId)
            .value();
    }

    getMottagningsplatser(dataSource: ITplArbetsorder[], endastDestinerade = true): IMottagningsplats[] {
        const handelssortiment = chain(dataSource)
            .map(x => x.handelssortiment)
            .flatten()
            .value();

        return chain(handelssortiment)
            .map(x => endastDestinerade ? x.destineringar : x.valbaraDestineringar)
            .flatten()
            .map(x => ({
                id: x.mottagningsplatsId,
                foradlatHandelssortimentId: x.foradlatHandelssortimentId
            }))
            .value();
    }

    allHandelssortiment(): IKeyValueGroup[] {
        return chain(values(this.handelssortimentDict))
            .flatten()
            .value();
    }

    handelssortimentMatchTransportsatt(grupp: string, isBransle: boolean): boolean {
        if (!grupp) {
            return true;
        }
        const isBranslegrupp = this.bransleGrupper.some(x => x === grupp);
        return isBransle && isBranslegrupp || !isBransle && !isBranslegrupp;
    }

    resolveHandelssortiment(handelssortimentId: number): IKeyValueGroup {
        return this.handelssortimentDict[handelssortimentId][0];
    }

    resolveBefraktare(befraktareId: number): IBefraktare {
        return this.befraktareDict[befraktareId][0];
    }

    resolveBefraktareAndGetName(befraktareId: number): string {
        const befraktare = this.resolveBefraktare(befraktareId);
        if (!befraktare) {
            return null;
        }
        if (!befraktare.huvudkod) {
            return befraktare.namn;
        }
        const befraktareLabel = befraktare.befraktare ? "Befraktare" : "fd Befraktare";
        return `${befraktare.huvudkod} ${befraktare.namn} (${befraktareLabel})`;
    }

    resolveHandelssortimentList(handelssortimentIdList: number[]): IKeyValueGroup[] {
        return handelssortimentIdList.map(handelssortimentId => this.handelssortimentDict[handelssortimentId][0]);
    }

    getUniqueHandelssortiment(arbetsorderList: ITplArbetsorder[]): IKeyValueGroup[] {
        const hsIdList = chain(arbetsorderList)
            .map(x => x.handelssortiment)
            .flatten()
            .map(x => x.handelssortimentId)
            .uniq()
            .value();
        const hslist = this.resolveHandelssortimentList(hsIdList);
        return chain(hslist).orderBy(x => x.value).value();
    }

    aoHasBefraktareAndAreDifferent(arbetsorder: ITplArbetsorder) {
        if (arbetsorder.handelssortiment.some(h => !!h.befraktareId)) {
            const firstId = arbetsorder.handelssortiment.find(h => !!h.befraktareId)?.befraktareId;

            return !arbetsorder.handelssortiment
                .every(h => h.befraktareId === firstId);
        }
        return false;
    }

    resolveMaskinlag(maskinlagId: number): IKeyValue {
        return this.maskinlagDict[maskinlagId][0];
    }

    resolveMaskinlagList(maskinlagIdList: number[]): IKeyValue[] {
        return maskinlagIdList.map(maskinlagId => this.maskinlagDict[maskinlagId][0]);
    }

    allMaskinlag(): IKeyValue[] {
        return flatMap(this.maskinlagDict);
    }

    allBefraktare(): IBefraktare[] {
        return flatMap(this.befraktareDict);
    }

    getMarknadsomraden(): Observable<MarknadsomradeDTO[]> {
        return this.apiClient.get<MarknadsomradeDTO[]>("verksamhetsomrade/marknadsomrade", false);
    }

    loadDestineringsomradeFilter(query: TplDestineringsomradefilterQuery): Observable<ITplDestineringsomradefilterQueryResult> {
        return this.apiClient.post<TplDestineringsomradefilterQuery, ITplDestineringsomradefilterQueryResult>("transportplanera/filter/destineringsomrade", query);
    }

    loadLinkedDestineringsomraden(query: TplDestineringsomradenMapQuery) {
        return this.apiClient.post<TplDestineringsomradenMapQuery, TplDestineringsomradenMapQueryResult>("transportplanera/filter/destineringsomrade/map", query);
    }

    getTplArbetsorderDetails(query: TplArbetsorderDetailsQuery): Observable<ITplArbetsorderDetails> {
        return this.apiClient.get<ITplArbetsorderDetails>("transportplanera/arbetsorder/detail", false, query);
    }

    getTplArbetsorderDestineringsrader(query: TplDestineringsraderQuery): Observable<TplAoRowsModel> {
        return this.apiClient.get<TplAoRowsModel>("transportplanera/arbetsorder/destineringsrader", false, query);
    }

    getRutt(query: TplDestineringRuttQuery): Observable<ITplDestineringRuttQueryResult> {
        return this.apiClient.get<ITplDestineringRuttQueryResult>("transportplanera/rutt", false, query);
    }

    loadNyRuttGeometry(query: TplKvvNyRuttQuery): Observable<ITplKvvNyRuttQueryResult> {
        return this.apiClient.post<TplKvvNyRuttQuery, ITplKvvNyRuttQueryResult>("transportplanera/rutt/forslag", query);
    }

    getBegransningsForslag(query: VirkesorderBegransningsforslagQuery): Observable<Destinering[]> {
        return this.apiClient.get<Destinering[]>("transportplanera/arbetsorder/begransningsforslag", false, query);
    }

    getMottagninsplatsLeveransplan(mottagningsplatsId: number): Observable<ITplMottagningsplatsLeveransplan> {
        return this.apiClient.get<ITplMottagningsplatsLeveransplan>(`transportplanera/mottagningsplats/${mottagningsplatsId}/leveransplan`, false);
    }

    getHandelser(query: TplGetHandelserQuery): Observable<ITplHandelser> {
        return this.apiClient.get<ITplHandelser>("transportplanera/handelse", false, query);
    }

    isExternArbetsorder(arbetsorder: ITplArbetsorderDetails) {
        return ((arbetsorder.anskaffningsform === Anskaffningsformer.EgenTerminal && !arbetsorder.viol3) ||
                (arbetsorder.anskaffningsform === Anskaffningsformer.Import && !arbetsorder.viol3) ||
                arbetsorder.anskaffningsform === Anskaffningsformer.CentralaKop) &&
                arbetsorder.typ === Arbetsordertyper.IngenProduktion;
    }

    loadProduktionsomradeFilter(query: TplProduktionsomradefilterQuery): Observable<ITplProduktionsomradefilterQueryResult> {
        return this.apiClient.post<TplProduktionsomradefilterQuery, ITplProduktionsomradefilterQueryResult>("transportplanera/filter/produktionsomrade", query);
    }
}
